// @ts-nocheck

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import WordGraph, { WordGraphNode, WordGraphLink } from '../components/WordGraph';
import WordMatrix from '../components/WordMatrix';

type Word = {
  text: string;
  value: number;
  sentiment: number;  // Range from -1 to 1
  emotions: {
    Happy: number;
    Sad: number;
    Angry: number;
    Fear: number;
    Surprise: number;
  };
  context: string[];  // Array of sample responses showing different contexts
  [key: string]: any;
};

// Add this function near the top of the file, before SingleWordCloud component
const getSentimentColor = (sentiment: number): string => {
  return d3.scaleLinear<string>()
    .domain([-0.3, 0, 0.3])
    .range(['#e74c3c', '#95a5a6', '#2ecc71'])(sentiment);
};

const SingleWordCloud: React.FC<{ 
  words: Word[]; 
  title: string; 
  colorByDiffType?: boolean;
  showLegend?: boolean;
}> = ({ words, title, colorByDiffType, showLegend = true }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!words.length) return;
    const width = 450;
    const height = 450;
    const margin = 40;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const sizeScale = (value: number) => Math.log(value + 20) * 6;

    // Modern color palette for word clouds
    const colorPalette = colorByDiffType ? {
      common: "#27ae60",
      uniquePre: "#e74c3c",
      uniquePost: "#3498db"
    } : {
      positive: "#2ecc71",
      neutral: "#95a5a6",
      negative: "#e74c3c"
    };

    // Comparison cloud colors - more muted versions
    const comparisonColors = {
      common: "#27ae60",
      uniquePre: "#e74c3c",
      uniquePost: "#3498db"
    };

    const layout = cloud()
      .size([width - margin * 2, height - margin * 2])
      .words(words.map(d => ({ ...d, size: sizeScale(d.value) })))
      .padding(3) // Slightly more spacing between words
      .rotate(() => ~~(Math.random() * 2) * 90)
      .font("Impact")
      .fontSize(d => d.size)
      .on("end", draw);

    layout.start();

    function draw(words: any[]) {
      const g = svg.append("g")
        .attr("transform", `translate(${width / 2},${height / 2})`);
      g.selectAll("text")
        .data(words)
        .enter().append("text")
        .style("font-size", d => `${d.size}px`)
        .style("font-family", "Impact")
        .style("fill", (d: any) => {
          if (colorByDiffType) {
            if (d.diffType === "common") return comparisonColors.common;
            if (d.diffType === "unique-pre") return comparisonColors.uniquePre;
            if (d.diffType === "unique-post") return comparisonColors.uniquePost;
          } else {
            // Use sentiment-based coloring
            return getSentimentColor(d.sentiment || 0);
          }
        })
        .style("opacity", d => {
          // Add subtle opacity variation based on word size
          return 0.7 + (d.size / 100 * 0.3); // Range from 0.7 to 1.0
        })
        .attr("text-anchor", "middle")
        .attr("transform", d => `translate(${d.x},${d.y}) rotate(${d.rotate})`)
        .text(d => d.text)
        .each(function(this: SVGTextElement, d: Word) {
          const emotions = d.emotions ? Object.entries(d.emotions)
            .map(([emotion, value]) => `${emotion}: ${((value as number) * 100).toFixed(0)}%`)
            .join('<br/>') : '';
          
          const contextExamples = d.context && d.context.length > 0 
            ? `<br/><br/><em>Sample contexts:</em><br/>${d.context.slice(0, 3).join('<br/>')}`
            : '';

          const sentimentText = d.sentiment 
            ? `<br/><strong>Sentiment:</strong> ${d.sentiment > 0 ? 'Positive' : d.sentiment < 0 ? 'Negative' : 'Neutral'} (${(d.sentiment * 100).toFixed(0)}%)`
            : '';

          tippy(this, {
            content: `
              <div style="font-family: 'Montserrat, sans-serif';">
                <h3 style="margin: 0 0 8px; color: #2c3e50;">${d.text}</h3>
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                  <strong>Mentions:</strong> ${d.value}
                  <span style="margin-left: 15px;">${d.diffType ? `(${d.diffType.replace('-', ' ')})` : ''}</span>
                </div>
                ${sentimentText}
                <div style="margin: 10px 0; padding: 8px; background: rgba(52, 152, 219, 0.1); border-radius: 4px;">
                  <strong>Emotional Analysis:</strong><br/>
                  ${emotions}
                </div>
                ${d.related && d.related.length > 0 ? `
                  <div style="margin: 8px 0;">
                    <strong>Related Themes:</strong><br/>
                    ${d.related.slice(0, 3).join(', ')}
                  </div>
                ` : ''}
                ${contextExamples ? `
                  <div style="margin-top: 8px; font-style: italic;">
                    ${contextExamples}
                  </div>
                ` : ''}
              </div>
            `,
            allowHTML: true,
            animation: 'scale',
            theme: 'light-border',
            maxWidth: 300
          });
        });
    }
  }, [words, colorByDiffType, title]);

  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <h2>{title}</h2>
      <div style={{ position: 'relative' }}>
        <svg ref={svgRef} width={400} height={400}></svg>
        {showLegend && (
          <div style={{ 
            position: 'absolute', 
            bottom: -50, 
            left: '50%', 
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '10px',
            fontSize: '12px',
            fontFamily: 'Montserrat, sans-serif',
          }}>
            {colorByDiffType ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#27ae60', marginRight: 4 }}></span>
                  Common
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#e74c3c', marginRight: 4 }}></span>
                  Pre-Show
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#3498db', marginRight: 4 }}></span>
                  Post-Show
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#2ecc71', marginRight: 4 }}></span>
                  Positive
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#95a5a6', marginRight: 4 }}></span>
                  Neutral
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#e74c3c', marginRight: 4 }}></span>
                  Negative
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {words.length > 0 && (
        <div style={{
          marginTop: '60px',
          padding: '15px',
          backgroundColor: 'rgba(52, 152, 219, 0.1)',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'Montserrat, sans-serif',
        }}>
          <strong>Theme Summary:</strong>
          <br />
          Total Themes: {words.length}
          <br />
          Most Frequent: {words[0]?.text} ({words[0]?.value} mentions)
          <br />
          Average Sentiment: {
            (words.reduce((acc, w) => acc + (w.sentiment || 0), 0) / words.length).toFixed(2)
          }
          <br />
        </div>
      )}
    </div>
  );
};

const WordCloudVis = () => {
  const [preWords, setPreWords] = useState<Word[]>([]);
  const [postWords, setPostWords] = useState<Word[]>([]);
  const [compWords, setCompWords] = useState<Word[]>([]);
  const [graphNodes, setGraphNodes] = useState<WordGraphNode[]>([]);
  const [graphLinks, setGraphLinks] = useState<WordGraphLink[]>([]);
  const [matrixData, setMatrixData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/wordcloud_themes_pre_improved.json').then(res => res.json()),
      fetch('/wordcloud_themes_post_improved.json').then(res => res.json())
    ])
      .then(([preData, postData]) => {
        // Extract the wordcloud_data array from the new structure
        const pre = preData.wordcloud_data || [];
        const post = postData.wordcloud_data || [];

        // Filter out any objects without a text property
        const filteredPre = pre.filter((w: any) => typeof w.text === "string" && w.text.length > 0);
        const filteredPost = post.filter((w: any) => typeof w.text === "string" && w.text.length > 0);

        const preSet = new Set(filteredPre.map(w => w.text.toLowerCase()));
        const postSet = new Set(filteredPost.map(w => w.text.toLowerCase()));

        const sortedPre = filteredPre
          .map(w => ({
            ...w,
            diffType: postSet.has(w.text.toLowerCase()) ? "common" : "unique-pre"
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 15);

        const sortedPost = filteredPost
          .map(w => ({
            ...w,
            diffType: preSet.has(w.text.toLowerCase()) ? "common" : "unique-post"
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 15);

        // Build a map for quick lookup
        const preMap = new Map<string, Word>(sortedPre.map((w: Word) => [w.text.toLowerCase(), w]));
        const postMap = new Map<string, Word>(sortedPost.map((w: Word) => [w.text.toLowerCase(), w]));

        // Combine all unique words from both sets
        const allWords = Array.from(new Set([
          ...sortedPre.map(w => w.text.toLowerCase()),
          ...sortedPost.map(w => w.text.toLowerCase())
        ]));

        // For each word, determine diffType and value (use max value for visibility)
        const comparison = allWords.map(text => {
          const preWord = preMap.get(text);
          const postWord = postMap.get(text);
          let diffType = "common";
          if (preWord && !postWord) diffType = "unique-pre";
          else if (!preWord && postWord) diffType = "unique-post";
          
          const word = preWord || postWord;
          return {
            text: word?.text ?? "",
            value: (preWord?.value ?? 0) + (postWord?.value ?? 0),
            diffType,
            related: word?.related ?? [],
            sentiment: word?.sentiment ?? 0,
            emotions: word?.emotions ?? {
              Happy: 0,
              Sad: 0,
              Angry: 0,
              Fear: 0,
              Surprise: 0
            },
            context: word?.context ?? []
          };
        }).sort((a, b) => b.value - a.value).slice(0, 30);

        setPreWords(sortedPre);
        setPostWords(sortedPost);
        setCompWords(comparison);

        // --- Build word graph data ---
        // Use top 20 words from both pre and post for nodes
        const nodeMap = new Map<string, WordGraphNode>();
        [...sortedPre, ...sortedPost].slice(0, 20).forEach(w => {
          nodeMap.set(w.text, {
            id: w.text,
            group: w.diffType || 'common',
            value: w.value,
            sentiment: typeof w.sentiment === 'number' ? w.sentiment : undefined
          });
        });
        // Build edges based on 'related' field (if present), allowing substring matches
        const nodeKeys = Array.from(nodeMap.keys());
        const linkCounts: { [key: string]: number } = {};
        nodeMap.forEach((node, text) => {
          const word = preMap.get(text.toLowerCase()) || postMap.get(text.toLowerCase());
          if (word && Array.isArray(word.related)) {
            word.related.forEach((rel: string) => {
              const relKey = rel.trim().toLowerCase();
              nodeKeys.forEach(other => {
                // Allow substring match in either direction
                const otherKey = other.trim().toLowerCase();
                if (
                  relKey === otherKey ||
                  relKey.includes(otherKey) ||
                  otherKey.includes(relKey)
                ) {
                  const key = [text, other].sort().join('||');
                  linkCounts[key] = (linkCounts[key] || 0) + 1;
                }
              });
            });
          }
        });
        let links: WordGraphLink[] = Object.entries(linkCounts).map(([key, value]) => {
          const [source, target] = key.split('||');
          return { source, target, value };
        });
        // If still no links, try to create links based on co-occurrence in keywords array (if available)
        if (links.length === 0) {
          // For each word, if it has a keywords array, create edges between all pairs of keywords in that array (if those keywords are nodes)
          nodeMap.forEach((node, text) => {
            const word = preMap.get(text.toLowerCase()) || postMap.get(text.toLowerCase());
            if (word && Array.isArray(word.keywords)) {
              const keywords = word.keywords.map((k: string) => k.trim().toLowerCase());
              for (let i = 0; i < keywords.length; i++) {
                for (let j = i + 1; j < keywords.length; j++) {
                  const k1 = keywords[i];
                  const k2 = keywords[j];
                  // Only add edge if both keywords are nodes
                  const n1 = nodeKeys.find(n => n.trim().toLowerCase() === k1);
                  const n2 = nodeKeys.find(n => n.trim().toLowerCase() === k2);
                  if (n1 && n2) {
                    const key = [n1, n2].sort().join('||');
                    linkCounts[key] = (linkCounts[key] || 0) + 1;
                  }
                }
              }
            }
          });
          links = Object.entries(linkCounts).map(([key, value]) => {
            const [source, target] = key.split('||');
            return { source, target, value };
          });
        }
        // If still no links, create edges between nodes whose text are substrings of each other
        if (links.length === 0) {
          for (let i = 0; i < nodeKeys.length; i++) {
            for (let j = i + 1; j < nodeKeys.length; j++) {
              const t1 = nodeKeys[i].trim().toLowerCase();
              const t2 = nodeKeys[j].trim().toLowerCase();
              if (t1.length > 2 && t2.length > 2 && (t1.includes(t2) || t2.includes(t1))) {
                links.push({ source: nodeKeys[i], target: nodeKeys[j], value: 1 });
              }
            }
          }
        }
        setGraphNodes(Array.from(nodeMap.values()));
        setGraphLinks(links);

        // --- Build matrix data ---
        // Use top 15 themes from both pre and post
        const matrixThemes = [...sortedPre, ...sortedPost]
          .reduce((acc, w) => {
            const existing = acc.find(item => item.text.toLowerCase() === w.text.toLowerCase());
            if (existing) {
              // This part works correctly - updates preValue and postValue
              existing.preValue = existing.preValue || 0;
              existing.postValue = existing.postValue || 0;
              if (sortedPre.includes(w)) {
                existing.preValue = w.value;
                existing.preSentiment = w.sentiment;
                existing.preEmotions = w.emotions;
              }
              if (sortedPost.includes(w)) {
                existing.postValue = w.value;
                existing.postSentiment = w.sentiment;
                existing.postEmotions = w.emotions;
              }
              
              // Calculate weighted averages based on mention counts
              const totalMentions = existing.preValue + existing.postValue;
              existing.value = totalMentions; // ADD THIS LINE - it's missing!
              
              if (existing.preSentiment !== undefined && existing.postSentiment !== undefined) {
                existing.sentiment = (existing.preSentiment * existing.preValue + existing.postSentiment * existing.postValue) / totalMentions;
                
                // Average emotions weighted by mention counts
                existing.emotions = {};
                Object.keys(existing.preEmotions || {}).forEach(emotion => {
                  const preWeight = existing.preValue / totalMentions;
                  const postWeight = existing.postValue / totalMentions;
                  existing.emotions[emotion] = (existing.preEmotions[emotion] * preWeight) + (existing.postEmotions[emotion] * postWeight);
                });
              } else {
                // Only one dataset has this theme
                existing.sentiment = existing.preSentiment || existing.postSentiment || w.sentiment;
                existing.emotions = existing.preEmotions || existing.postEmotions || w.emotions;
              }
            } else {
              const preValue = sortedPre.includes(w) ? w.value : 0;
              const postValue = sortedPost.includes(w) ? w.value : 0;
              acc.push({
                text: w.text,
                value: preValue + postValue, // This works correctly for new entries
                preValue: preValue,
                postValue: postValue,
                sentiment: w.sentiment,
                emotions: w.emotions,
                preSentiment: sortedPre.includes(w) ? w.sentiment : undefined,
                postSentiment: sortedPost.includes(w) ? w.sentiment : undefined,
                preEmotions: sortedPre.includes(w) ? w.emotions : undefined,
                postEmotions: sortedPost.includes(w) ? w.emotions : undefined
              });
            }
            return acc;
          }, [] as any[])
          .slice(0, 15);
        setMatrixData(matrixThemes);

        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading word cloud data:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        height: '500px',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#3498db',
        fontFamily: 'Montserrat, sans-serif',
        fontSize: '20px',
      }}>
        <div>Generating visualization...</div>
      </div>
    );
  }

  // Debug: log graph data before rendering
  console.log('WordGraph nodes:', graphNodes);
  console.log('WordGraph links:', graphLinks);

  return (
    <>
      <div style={{ 
        borderRadius: '12px',
        padding: '30px',
      }}>
        <div style={{
          backgroundColor: 'rgba(52, 73, 94, 0.05)',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '30px',
          fontFamily: 'Montserrat, sans-serif',
        }}>
          <h2 style={{
            color: '#2c3e50',
            marginBottom: '15px',
            fontSize: '20px',
            borderBottom: '2px solid #3498db',
            paddingBottom: '8px'
          }}>Theme Analysis Overview</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
          }}>
            <div>
              <h3 style={{ color: '#3498db', marginBottom: '8px', fontSize: '16px' }}>Pre-Show Insights</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li>Total Themes: {preWords.length}</li>
                <li>Most Common: {preWords[0]?.text} ({preWords[0]?.value} mentions)</li>
                {/* <li>Coverage: ~80% of valid responses</li> */}
                <li>Average Sentiment: {(preWords.reduce((acc, w) => acc + (w.sentiment || 0), 0) / preWords.length).toFixed(2)}</li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: '#e74c3c', marginBottom: '8px', fontSize: '16px' }}>Post-Show Insights</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li>Total Themes: {postWords.length}</li>
                <li>Most Common: {postWords[0]?.text} ({postWords[0]?.value} mentions)</li>
                {/* <li>Coverage: ~80% of valid responses</li> */}
                <li>Average Sentiment: {(postWords.reduce((acc, w) => acc + (w.sentiment || 0), 0) / postWords.length).toFixed(2)}</li>
              </ul>
            </div>
            {/* <div>
              <h3 style={{ color: '#27ae60', marginBottom: '8px', fontSize: '16px' }}>Theme Evolution</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li>Common Themes: {compWords.filter(w => w.diffType === 'common').length}</li>
                <li>New Themes: {compWords.filter(w => w.diffType === 'unique-post').length}</li>
                <li>Dropped Themes: {compWords.filter(w => w.diffType === 'unique-pre').length}</li>
              </ul>
            </div> */}
          </div>
        </div>
        <h1 style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 800,
          fontSize: 34,
          textAlign: 'center',
          color: '#34495e',
          marginBottom: 40,
        }}>Word Cloud Analysis</h1>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '30px',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100%',
          marginBottom: 50
        }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h2 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: 24,
              color: '#3498db',
              marginBottom: 8,
              position: 'relative',
              display: 'inline-block',
              padding: '0 10px 8px',
              borderBottom: '3px solid #3498db',
              marginLeft: '6rem'
            }}>Pre-Show Themes</h2>
            <SingleWordCloud words={preWords} title="" />
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h2 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: 24,
              color: '#e74c3c',
              marginBottom: 8,
              position: 'relative',
              display: 'inline-block',
              padding: '0 10px 8px',
              borderBottom: '3px solid #e74c3c',
              marginLeft: '6rem'
            }}>Post-Show Themes</h2>
            <SingleWordCloud words={postWords} title="" />
          </div>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          marginBottom: 24,
          padding: '24px',
          background: 'rgba(56, 57, 76, 0.5)',
          borderRadius: '12px'
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: 24,
            color: '#27ae60',
            marginBottom: 16,
            position: 'relative',
            display: 'inline-block',
            padding: '0 10px 8px',
            borderBottom: '3px solid #27ae60',
            marginLeft: '6rem'
          }}>Theme Comparison</h2>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '40px',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%'
          }}>
            <SingleWordCloud words={compWords} title="" colorByDiffType />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginTop: 24,
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 15
          }}>
            <span style={{
              color: "#27ae60",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(39, 174, 96, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#27ae60', marginRight: 8 }}></div>
              Common Themes
            </span>
            <span style={{
              color: "#e74c3c",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(231, 76, 60, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e74c3c', marginRight: 8 }}></div>
              Pre-Show Only
            </span>
            <span style={{
              color: "#3498db",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(52, 152, 219, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3498db', marginRight: 8 }}></div>
              Post-Show Only
            </span>
          </div>
        </div>
      </div>
      {/* <WordGraph nodes={graphNodes} links={graphLinks} /> */}
      
      {/* Matrix Visualization */}
      <WordMatrix data={matrixData} />
    </>
  );
};

export default WordCloudVis;